import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import type { PortfolioType } from '@customTypes/portfolio';
import { datadogRum } from '@datadog/browser-rum';
import ErrorLabel from '@components/ErrorLabel';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
  portfolio?: PortfolioType;
};

const UpsertDialog = ({ handleClose, afterSubmit, portfolio }: Props) => {
  const isEditing = !!portfolio;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [hasMaxAge, setHasMaxAge] = useState(!!portfolio?.max_age);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    const maxAge = data.maxAge ? Number(data.maxAge) : 0;

    if (hasMaxAge && (maxAge < 18 || maxAge > 100)) {
      setErrors({ max_age: ['A idade máxima deve ser entre 18 e 100 anos.'] });
      return;
    }

    setLoading(true);

    const params = {
      name: capitalizeFirstLetter(data.name as string),
      max_age: hasMaxAge ? Number(data.maxAge) : null,
    };
    
    try {
      let response;

      if (isEditing) {
        response = await api.put(`/portfolio/${portfolio.id}`, {
          ...params,
        });
      } else {
        response = await api.post('/portfolio', {
          ...params,
        });
      }

      if (response?.status === 200) {
        toast.success('Portfólio salvo com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error('Erro ao salvar portfólio. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to save portfolio'), {
        error,
        portfolioId: portfolio?.id,
        params,
      });
    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                {isEditing ? 'Editar portfólio' : 'Criar portfólio'}
              </Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.name}>
                  <Field.Label>Nome</Field.Label>
                  <InputGroup width="full" gap={0}>
                    <Input
                      name="name"
                      placeholder="Digite aqui..."
                      minLength={3}
                      defaultValue={portfolio?.name || ''}
                      variant="subtle"
                      required
                    />
                  </InputGroup>
                  {errors?.name?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Box my={4}>
                  <Checkbox.Root
                    checked={hasMaxAge}
                    onCheckedChange={(e) => setHasMaxAge(e.checked as boolean)}
                    cursor="pointer"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control colorPalette="argos" />
                    <Checkbox.Label fontWeight={400}>
                      Idade máxima
                    </Checkbox.Label>
                  </Checkbox.Root>
                </Box>

                {hasMaxAge && (
                  <Field.Root invalid={!!errors?.max_age}>
                    <InputGroup width="full" gap={0}>
                      <Input
                        name="maxAge"
                        placeholder="Digite aqui..."
                        minLength={3}
                        defaultValue={portfolio?.max_age || ''}
                        variant="subtle"
                        type="number"
                        required
                      />
                    </InputGroup>
                    {errors?.max_age?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>
                )}

                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UpsertDialog;
